"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOffsetCursorPaginationAdapter = void 0;
const react_1 = require("react");
const ui_components_1 = require("@groupby/ui-components");
const pagination_utils_1 = require("./pagination.utils");
const client_constants_1 = require("../../client/client.constants");
const apollo_client_utils_1 = require("../../../apollo-client/apollo-client.utils");
/**
 * Pagination adapter to connect Advanced DataGrid pagination with GraphQL offset-based cursor pagination.
 *
 * **Important**: This hook handles bidirectional synchronization:
 * 1. User changes page in UI → triggers GraphQL refetch with new cursor variables
 * 2. External refetch (e.g., filter change) → uses pageInfo.startCursor to sync current page
 *
 * **Key insight**: pageInfo.startCursor always points to the first element on the current page,
 * regardless of pagination direction (forward or backward). This makes it the most reliable
 * way to determine the current page.
 */
const useOffsetCursorPaginationAdapter = ({ refetch, totalCount, startCursor, persistedState, initialModel, setPersistedState, }) => {
    const pageSize = (0, react_1.useMemo)(() => { var _a; return (_a = persistedState === null || persistedState === void 0 ? void 0 : persistedState.pageSize) !== null && _a !== void 0 ? _a : client_constants_1.PAGE_SIZE_DEFAULT; }, [persistedState === null || persistedState === void 0 ? void 0 : persistedState.pageSize]);
    const { paginationModel, setPage, setPageSize, } = (0, ui_components_1.usePagination)({
        initialModel,
        setPersistedState,
    });
    const { page: currentPage, pageSize: currentPageSize } = paginationModel;
    (0, react_1.useEffect)(() => {
        if (pageSize !== currentPageSize) {
            setPageSize(pageSize);
        }
        // We only want to run this effect when pageSize changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize]);
    (0, react_1.useEffect)(() => {
        if (startCursor) {
            const pageFromCursor = (0, pagination_utils_1.parsePageNumberFromCursor)(startCursor, currentPageSize);
            if (pageFromCursor !== currentPage) {
                setPage(pageFromCursor);
            }
        }
        // We only want to run this effect when startCursor changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startCursor]);
    // Helper to determine if we're on the last page
    const isLastPage = (0, react_1.useCallback)((page, pageSize, total) => {
        if (!total)
            return false;
        const totalPages = Math.ceil(total / pageSize);
        return page === totalPages - 1; // 0-based page index
    }, []);
    const handlePaginationRefetchError = (0, react_1.useCallback)((error) => {
        if (!(0, apollo_client_utils_1.isAbortError)(error)) {
            // Non-abort failures are surfaced by Apollo query state.
        }
    }, []);
    // Convert page/pageSize to GraphQL pagination variables
    const getPaginationVariables = (0, react_1.useCallback)((nextPage, currentPage, pageSize) => {
        const step = nextPage - currentPage;
        // First page - simple query
        if (nextPage === 0) {
            return (0, pagination_utils_1.getFirst)(pageSize);
        }
        // Last page - use backward pagination with correct page size
        if (isLastPage(nextPage, pageSize, totalCount)) {
            return (0, pagination_utils_1.getLast)((0, pagination_utils_1.calculateLastPageSize)(pageSize, totalCount), totalCount);
        }
        // Sequential backward navigation
        // Use the startCursor from pageInfo as the 'before' for going back
        // Example: if we're on page 2 (startCursor=OffsetConnection:24),
        // going back means: last=24, before=OffsetConnection:24
        if (step === -1 && startCursor) {
            return (0, pagination_utils_1.getBack)(startCursor, pageSize);
        }
        // Any other navigation (forward or jumps) - use offset-based pagination
        const offset = nextPage * pageSize;
        return (0, pagination_utils_1.getOffset)(offset, pageSize);
    }, [totalCount, isLastPage, startCursor]);
    // Handle pagination changes from DataGrid
    const onPaginationModelChange = (0, react_1.useCallback)((model) => {
        // Handle page size change - reset to first page
        if (model.pageSize !== currentPageSize) {
            setPageSize(model.pageSize);
            const variables = (0, pagination_utils_1.getFirst)(model.pageSize);
            void refetch(variables).catch(handlePaginationRefetchError);
            return;
        }
        // Handle page change
        if (model.page !== currentPage) {
            setPage(model.page);
            const variables = getPaginationVariables(model.page, currentPage, model.pageSize);
            void refetch(variables).catch(handlePaginationRefetchError);
        }
    }, [
        currentPageSize,
        currentPage,
        getPaginationVariables,
        handlePaginationRefetchError,
        refetch,
        setPage,
        setPageSize,
    ]);
    return {
        paginationModel,
        onPaginationModelChange,
    };
};
exports.useOffsetCursorPaginationAdapter = useOffsetCursorPaginationAdapter;
